import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle, AlertTriangle, Building2, CheckCircle2, CornerDownRight, FileText,
  Info, Lightbulb, ListChecks, Loader2, Plus, Trash2, Undo2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useSubmitMeetingReport } from "@/hooks/useClient360Meetings";
import { useRequestTypes } from "@/hooks/useInternalRequests";
import {
  getReportDraft, removeOnBehalfTmpFile, saveReportDraft,
  submitMeetingReportOnBehalf, uploadOnBehalfTmpFile,
  type AcceptedAction, type ReportDraft,
  type ReportOutcome, type ReportRiskMarker, type SubmitReportPayload,
  type TmpFileResult,
} from "@/api/client360Meetings";
import type { OnBehalfProceedContext } from "@/components/meetings/OnBehalfConfirmModal";
import { markNotOccurred, rescheduleInteraction } from "@/api/interactions";
import { ApiClientError } from "@/api/client";
import { TaskCreateDrawer, type StagedTaskSpec } from "@/components/task/TaskCreateDrawer";
import { CreateInternalRequestDrawer, type StagedRequestSpec } from "@/components/requests/CreateInternalRequestDrawer";
import { AttachmentUpload, type PendingAttachment } from "@/components/attachments/AttachmentUpload";
import type { Attachment } from "@/api/types";

/**
 * D-055 S3 — staged-картка Actions Builder (visual v0.2 екран 4).
 * Spec зберігається ЛОКАЛЬНО до submit; server створює atomic.
 */
interface StagedCard {
  key: string;
  kind: AcceptedAction["kind"];
  title: string;
  meta: string;
  action: AcceptedAction;
  /** route_missing (pre-submit каталог АБО server 422) — блокує submit. */
  blocked?: string | null;
}

/** §8 suggestion-whitelist (mirror ProfileSuggestionService::APPLICABLE_FIELDS). */
const SUGGESTION_FIELDS: { code: string; label: string }[] = [
  { code: "cCCompetitorBrands", label: "Бренди конкурентів" },
  { code: "cCCompetitorNote", label: "Нотатка про конкурентів" },
  { code: "cCCompetitorDominance", label: "Домінування конкурента" },
  { code: "cCPotentialEstimate", label: "Оцінка потенціалу" },
  { code: "cCPotentialReasons", label: "Обґрунтування потенціалу" },
  { code: "cCPurchasingModel", label: "Модель закупівлі" },
  { code: "cCDecisionModel", label: "Модель прийняття рішень" },
  { code: "cCOrderFrequency", label: "Частота замовлень" },
  { code: "cCManagerComment", label: "Коментар менеджера" },
  { code: "cCNextCommercialAction", label: "Наступна комерційна дія" },
  { code: "cCAssortmentFocus", label: "Асортиментний фокус" },
];

/**
 * D-055 S2 — Meeting Report Wizard C+ (D-054; visual package v0.2 екран 3).
 *
 * C+ = structured WRITTEN report, не анкета: письмовий debrief REQUIRED +
 * результат (8 чіпів = curated UI поверх live enum `outcome`, resultTags НЕ
 * існує) + riskMarkers + next-action (дія АБО explicit причина; suggest, НЕ
 * force-type). Server-enforced (ReportCPlusPolicy): blacklist «все ок»,
 * minLength 40/120-risk — клієнт лише дзеркалить ліміти, авторитет = сервер.
 *
 * Не відбулась → ОКРЕМІ 3.1A endpoint-и (/not-occurred, /reschedule inline) —
 * debrief/чіпи не вимагаються (§4 not_happened). Чернетка автозберігається
 * (optimistic-lock baseModifiedAt); submit атомарний (AC-3/AC-9).
 */

export interface ReportTarget {
  interactionId: string;
  summary: string;
}

/** D-151 S2 — детермінований хеш payload-а для idempotency (сервер порівнює рівність). */
function obHash(input: string): string {
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 = ((h1 * 33) ^ c) >>> 0;
    h2 = ((h2 * 31) + c) >>> 0;
  }
  return h1.toString(16) + h2.toString(16);
}

type NotReason = "cancelled" | "noshow" | "rescheduled" | "other";

const NOT_REASONS: { v: NotReason; label: string }[] = [
  { v: "cancelled", label: "Клієнт скасував" },
  { v: "noshow", label: "Ми не змогли" },
  { v: "rescheduled", label: "Перенесено" },
  { v: "other", label: "Інша причина" },
];

const OUTCOME_GROUPS: { title: string; risk: boolean; chips: { v: ReportOutcome; label: string }[] }[] = [
  {
    title: "🟢 Позитивні", risk: false,
    chips: [
      { v: "agreed", label: "Домовились" },
      { v: "interested", label: "Зацікавлений" },
      { v: "followup", label: "Follow-up" },
    ],
  },
  {
    title: "⚪ Нейтральні", risk: false,
    chips: [{ v: "notready", label: "Не готовий" }],
  },
  {
    title: "🟠 Потребує уваги (RISK)", risk: true,
    chips: [
      { v: "contract", label: "Питання договору" },
      { v: "payment", label: "Оплата / борг" },
      { v: "complaint", label: "Скарга" },
      { v: "escalate", label: "Залучити керівника" },
    ],
  },
];

const RISK_MARKERS: { v: ReportRiskMarker; label: string }[] = [
  { v: "competitor", label: "Конкурент" },
  { v: "client_refusal", label: "Ризик відмови" },
  { v: "lost_risk", label: "Ризик втрати клієнта" },
];

export function MeetingReportDrawer({
  target,
  holdingId,
  onClose,
  onBehalfCtx = null,
}: {
  target: ReportTarget | null;
  holdingId: string;
  onClose: () => void;
  /**
   * D-151 S2 — supervisor-режим (Q4-A): write-free до submit (P47) — БЕЗ
   * draftGet/autosave/Attachment; файли → tmp-транспорт (P67); submit →
   * report-on-behalf з reason/confirm/override/CAS/idempotency з confirm-кроку.
   */
  onBehalfCtx?: OnBehalfProceedContext | null;
}) {
  const isOnBehalf = onBehalfCtx !== null;
  const open = target !== null;
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();
  const mutation = useSubmitMeetingReport(holdingId);
  const qc = useQueryClient();

  const invalidateMeetings = () => {
    qc.invalidateQueries({ queryKey: ["client360Meetings", holdingId] });
    qc.invalidateQueries({ queryKey: ["client360Timeline", holdingId] });
    qc.invalidateQueries({ queryKey: ["homeSummary"] });
  };

  // крок 1 — чи відбулась
  const [occurred, setOccurred] = useState<"yes" | "not">("yes");
  const [notReason, setNotReason] = useState<NotReason>("cancelled");
  const [notComment, setNotComment] = useState("");
  const [reschedDate, setReschedDate] = useState("");
  const [reschedTime, setReschedTime] = useState("");

  // C+ поля
  const [text, setText] = useState("");
  const [outcome, setOutcome] = useState<ReportOutcome | null>(null);
  const [riskMarkers, setRiskMarkers] = useState<ReportRiskMarker[]>([]);
  const [decision, setDecision] = useState<"action" | "none" | null>(null);
  const [noActionReason, setNoActionReason] = useState("");

  // TASK-ATT v1.3 (W3) — attachments staged in the server-draft (saveReportDraft)
  // so they survive autosave/optimistic-lock, atomic-bound on submit.
  // `attachmentsIds` is the FULL merged list (saved kept + pending) — send THIS.
  const [savedAtt, setSavedAtt] = useState<Attachment[]>([]);
  const [pendingAtt, setPendingAtt] = useState<PendingAttachment[]>([]);
  const [attIds, setAttIds] = useState<string[]>([]);

  // S3 — Actions Builder (staged; нічого не створено до submit)
  const [staged, setStaged] = useState<StagedCard[]>([]);
  const [stageDrawer, setStageDrawer] = useState<"task" | "follow_up" | "internal_request" | "suggestion" | null>(null);
  const [sugField, setSugField] = useState(SUGGESTION_FIELDS[0].code);
  const [sugValue, setSugValue] = useState("");
  const [sugNote, setSugNote] = useState("");
  const keyRef = useRef(0);
  const typesQ = useRequestTypes(open && decision === "action");

  // D-151 S2 — on-behalf: tmp-файли (P67) + стабільний clientRequestId на відкриття
  const [tmpFiles, setTmpFiles] = useState<TmpFileResult[]>([]);
  const [tmpUploading, setTmpUploading] = useState(false);
  const requestIdRef = useRef<string>("");

  // draft / lifecycle
  const [draft, setDraft] = useState<ReportDraft | null>(null);
  const [draftBase, setDraftBase] = useState<string | null>(null);
  const [draftState, setDraftState] = useState<"idle" | "saving" | "saved" | "error" | "conflict">("idle");
  const [done, setDone] = useState<{ tasks: number; kind: "report" | "not" | "resched" } | null>(null);
  const [err, setErr] = useState<{ code: string; message: string } | null>(null);
  const dirtyRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── reset + load draft on open ──────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setTmpFiles([]); setTmpUploading(false); requestIdRef.current = "";
      setOccurred("yes"); setNotReason("cancelled"); setNotComment("");
      setReschedDate(""); setReschedTime("");
      setText(""); setOutcome(null); setRiskMarkers([]);
      setDecision(null); setNoActionReason("");
      setSavedAtt([]); setPendingAtt([]); setAttIds([]);
      setStaged([]); setStageDrawer(null);
      setSugField(SUGGESTION_FIELDS[0].code); setSugValue(""); setSugNote("");
      setDraft(null); setDraftBase(null); setDraftState("idle");
      setDone(null); setErr(null);
      dirtyRef.current = false;
      return;
    }
    if (isOnBehalf) {
      // P47 — write-free: жодного draftGet (він assignee-gated) і жодного autosave.
      requestIdRef.current = (crypto as { randomUUID?: () => string }).randomUUID?.()
        ?? `ob${Date.now()}${Math.floor(Math.random() * 1e6)}`;
      setOccurred("yes");
      return;
    }
    const ctrl = new AbortController();
    getReportDraft(target!.interactionId, ctrl.signal)
      .then((d) => {
        setDraft(d);
        setDraftBase(d.modifiedAt);
        setText(d.text);
        setOutcome(d.outcome);
        setRiskMarkers(d.riskMarkers);
        // W3 — hydrate saved attachments from the server-draft so they survive
        // reload/lock; seed the merged id list with the persisted ids.
        const sa = d.attachments ?? [];
        setSavedAtt(sa);
        setPendingAtt([]);
        setAttIds(sa.map((a) => a.id));
        if (d.nextActionReason) { setDecision("none"); setNoActionReason(d.nextActionReason); }
      })
      .catch(() => { /* чернетки може не бути / немає доступу — форма лишається порожньою */ });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.interactionId]);

  // ── autosave (debounce 1.5s; лише occurred=yes; optimistic-lock) ────────
  const flushDraft = useCallback(() => {
    if (!target || !dirtyRef.current || draftState === "conflict") return;
    dirtyRef.current = false;
    setDraftState("saving");
    saveReportDraft(target.interactionId, {
      text,
      outcome,
      riskMarkers,
      nextActionReason: decision === "none" ? noActionReason : "",
      // W3 — stage FULL merged id list (saved kept + pending) into the
      // server-draft; link-multiple REPLACES the set on submit.
      attachmentsIds: attIds,
      baseModifiedAt: draftBase ?? undefined,
      // D-151 S2 (P73) — CAS-токен (mismatch → 409 revision_conflict).
      interactionRevision: draft?.interactionRevision ?? 0,
    })
      .then((r) => {
        setDraftBase(r.modifiedAt);
        // D-151 S2 (P73) — сервер інкрементує CAS на кожен guarded-write:
        // оновлюємо токен, інакше наступний autosave/submit ловить 409.
        setDraft((cur) => (cur ? { ...cur, interactionRevision: r.interactionRevision } : cur));
        setDraftState("saved");
      })
      .catch((e) => {
        if (e instanceof ApiClientError && e.code === "draft_conflict") setDraftState("conflict");
        else setDraftState("error");
        dirtyRef.current = true;
      });
  }, [target, text, outcome, riskMarkers, decision, noActionReason, attIds, draftBase, draftState]);

  useEffect(() => {
    if (!open || occurred !== "yes" || done || isOnBehalf) return;
    if (!dirtyRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushDraft, 1500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, occurred, text, outcome, riskMarkers, noActionReason, decision, attIds, done, flushDraft]);

  const touch = () => { dirtyRef.current = true; if (draftState === "saved") setDraftState("idle"); };

  if (!target) return null;

  // ── derived ─────────────────────────────────────────────────────────────
  const limits = draft?.limits ?? { minLengthNormal: 40, minLengthRisk: 120, riskOutcomes: ["complaint", "payment", "contract", "escalate"] };
  const isRisk = (outcome !== null && limits.riskOutcomes.includes(outcome)) || riskMarkers.length > 0;
  const minLen = isRisk ? limits.minLengthRisk : limits.minLengthNormal;
  const len = text.trim().length;

  // TASK-ATT v1.3 (W4 / D3=A) — C+ post-submit lock: attachments view/download
  // only once the report is submitted, UNLESS the supervisor returned it for
  // rework (reviewStatus === 'returned' → editable again). F2 form has NO lock.
  const attLocked = !!draft?.reportSubmitted && draft?.reviewStatus !== "returned";

  // S3 — staged cards: route-availability live-recheck (#9 каталог) — картка
  // запиту з недоступним маршрутом блокує submit (route-missing, екран 4).
  const routeTypes = typesQ.data?.types ?? [];
  const stagedChecked: StagedCard[] = staged.map((c) => {
    if (c.kind !== "internal_request") return c;
    const t = routeTypes.find((x) => x.type === c.action.requestType);
    if (routeTypes.length > 0 && (!t || !t.available)) {
      return { ...c, blocked: t?.reason ?? "Маршрут не налаштований / немає відповідального. Звіт НЕ подається, поки дію не видалено/замінено." };
    }
    return c.blocked && !c.blocked.startsWith("422:") ? { ...c, blocked: null } : c;
  });
  const blockedCards = stagedChecked.filter((c) => !!c.blocked);

  const stageKey = () => { keyRef.current += 1; return `a${keyRef.current}`; };

  const stageTask = (spec: StagedTaskSpec) => {
    const key = stageKey();
    setStaged((cur) => [...cur, {
      key,
      kind: spec.kind,
      title: spec.title,
      meta: `${spec.kind === "follow_up" ? "Follow-up" : "Задача"} · до ${spec.dueDate} · ${isOnBehalf ? "менеджеру (D3=A)" : "собі"}`,
      action: { kind: spec.kind, suggestionKey: key, title: spec.title, description: spec.description, dueDate: spec.dueDate },
    }]);
  };

  const stageRequest = (spec: StagedRequestSpec) => {
    const key = stageKey();
    setStaged((cur) => [...cur, {
      key,
      kind: "internal_request",
      title: spec.subject,
      meta: `${spec.requestTypeLabel}${spec.departmentName ? ` → ${spec.departmentName}` : ""} · маршрут CKormaxRequestRoute`,
      action: {
        kind: "internal_request", suggestionKey: key, requestType: spec.requestType,
        subject: spec.subject, body: spec.body, departmentId: spec.departmentId, formData: spec.formData,
      },
    }]);
  };

  const stageSuggestion = () => {
    if (sugValue.trim() === "") return;
    const key = stageKey();
    const f = SUGGESTION_FIELDS.find((x) => x.code === sugField)!;
    setStaged((cur) => [...cur, {
      key,
      kind: "suggestion",
      title: f.label,
      meta: `→ «${sugValue.trim()}» · review-flow, не перезапис`,
      action: {
        kind: "suggestion", suggestionKey: key, fieldCode: f.code, title: f.label,
        suggestedValue: sugValue.trim(), suggestedDisplay: sugValue.trim(),
        message: sugNote.trim() || undefined,
      },
    }]);
    setSugValue(""); setSugNote(""); setStageDrawer(null);
  };

  const removeStaged = (key: string) => setStaged((cur) => cur.filter((c) => c.key !== key));

  // W3 — attachments: AttachmentUpload emits the FULL merged id list; store it
  // and round-trip pending. touch() schedules the autosave-draft flush.
  const onAttChange = (next: { attachmentsIds: string[]; pending: PendingAttachment[] }) => {
    setAttIds(next.attachmentsIds);
    setPendingAtt(next.pending);
    touch();
  };
  // Remove SAVED = unlink: drop the id from saved + merged list (re-emit drops
  // it from the submitted set; backend unlinks, no physical delete).
  const onRemoveSaved = (id: string) => {
    setSavedAtt((cur) => cur.filter((a) => a.id !== id));
    setAttIds((cur) => cur.filter((x) => x !== id));
    touch();
  };

  const toggleMarker = (m: ReportRiskMarker) => {
    touch();
    setRiskMarkers((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]));
  };

  // client mirror (сервер авторитетний)
  const yesReady =
    len >= minLen
    && outcome !== null
    && decision !== null
    && blockedCards.length === 0
    && (decision === "action" ? staged.length > 0 : noActionReason.trim() !== "");
  const notReady =
    notReason === "rescheduled"
      ? reschedDate !== "" && reschedTime !== ""
      : notReason === "other"
        ? notComment.trim() !== ""
        : true;
  const canSubmit = !mutation.isPending && (occurred === "yes" ? yesReady : notReady);

  const debriefError = err !== null && ["debrief_required", "debrief_too_short", "debrief_blacklisted", "risk_debrief_too_short"].includes(err.code);

  // ── submit ──────────────────────────────────────────────────────────────
  const submit = async () => {
    setErr(null);
    try {
      if (isOnBehalf) {
        const res = await submitMeetingReportOnBehalf(target.interactionId, {
          reason: onBehalfCtx!.reason,
          confirmOccurredOnBehalf: onBehalfCtx!.confirmOccurredOnBehalf,
          overrideStaleDraft: onBehalfCtx!.overrideStaleDraft,
          expectedAssignedUserId: onBehalfCtx!.expectedAssignedUserId,
          interactionRevision: onBehalfCtx!.interactionRevision,
          clientRequestId: requestIdRef.current,
          requestHash: obHash(
            text.trim() + "|" + (outcome ?? "") + "|" + riskMarkers.join(",")
            + "|" + noActionReason.trim() + "|" + staged.map((c) => c.key).join(",")
            + "|" + tmpFiles.map((f) => f.tmpId).join(","),
          ),
          text: text.trim(),
          outcome: outcome ?? undefined,
          riskMarkers,
          nextActionReason: decision === "none" ? noActionReason.trim() : undefined,
          acceptedActions: decision === "action" ? staged.map((c) => c.action) : [],
          tmpFileIds: tmpFiles.map((f) => f.tmpId),
        });
        invalidateMeetings();
        qc.invalidateQueries({ queryKey: ["meetingsQueue"] });
        qc.invalidateQueries({ queryKey: ["meetingsControl"] });
        setDone({ tasks: (res.actions ?? []).length, kind: "report" });
      } else if (occurred === "yes") {
        const body: SubmitReportPayload = {
          occurred: "yes",
          text: text.trim(),
          outcome: outcome ?? undefined,
          riskMarkers,
          nextActionRequired: decision === "action" && staged.length > 0 ? "yes" : "no",
          nextActionReason: decision === "none" ? noActionReason.trim() : undefined,
          actions: [],
          // S3: staged-дії — створяться server-side атомарно зі звітом.
          acceptedActions: decision === "action" ? staged.map((c) => c.action) : [],
          // W3/W5 — FULL merged id list (saved kept + pending); link-multiple
          // REPLACES the bound set, atomic-bound with the report on submit.
          attachmentsIds: attIds,
          // D-151 S2 (P73) — CAS-токен з draftGet (обов'язковий server-side).
          interactionRevision: draft?.interactionRevision ?? 0,
        };
        const res = await mutation.mutateAsync({ interactionId: target.interactionId, body });
        setDone({ tasks: (res.actions ?? []).length, kind: "report" });
      } else if (notReason === "rescheduled") {
        await rescheduleInteraction(target.interactionId, {
          newDate: reschedDate,
          newTime: reschedTime,
          reason: notComment.trim() || undefined,
          interactionRevision: draft?.interactionRevision ?? 0,
        });
        invalidateMeetings();
        setDone({ tasks: 0, kind: "resched" });
      } else {
        await markNotOccurred(target.interactionId, {
          reason: notReason,
          comment: notComment.trim() || undefined,
          interactionRevision: draft?.interactionRevision ?? 0,
        });
        invalidateMeetings();
        setDone({ tasks: 0, kind: "not" });
      }
    } catch (e) {
      if (e instanceof ApiClientError) {
        setErr({ code: e.code, message: e.message });
        // S3: помилка дії адресується до картки (route_missing/submit-fail).
        const sk = (e.details as { suggestionKey?: string } | undefined)?.suggestionKey;
        if (sk) {
          setStaged((cur) => cur.map((c) => (c.key === sk ? { ...c, blocked: `422: ${e.message}` } : c)));
        }
      } else {
        setErr({ code: "unknown", message: e instanceof Error ? e.message : "Не вдалося подати звіт" });
      }
    }
  };

  const doneTitle =
    done?.kind === "report" ? "Звіт подано · зустріч завершено"
    : done?.kind === "resched" ? "Зустріч перенесено"
    : "Зафіксовано: зустріч не відбулась";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[480px] overflow-y-auto bg-background shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <FileText className="h-4 w-4 text-primary" aria-hidden /> Звіт про зустріч
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="space-y-4 px-5 py-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-success-foreground" aria-hidden />
            <div className="text-base font-semibold">{doneTitle}</div>
            <p className="text-sm text-muted-foreground">
              {done.kind === "report"
                ? done.tasks > 0
                  ? `Створено ${done.tasks} ${done.tasks === 1 ? "дію" : "дії(й)"} через canonical engines (задачі/запити — у своїх розділах, пропозиції — у Профілі на review).`
                  : "Зафіксовано «без дій» з причиною."
                : done.kind === "resched"
                  ? "Нову дату збережено; зустріч знову в статусі «Заплановано»."
                  : "Причину збережено. Завершеною («Проведено») зустріч не стане."}
            </p>
            <Button type="button" onClick={onClose} className="h-11 w-full text-sm font-semibold">Готово</Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 px-5 py-5">
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium">{target.summary}</div>

              {/* D-151 S2 — незмінна атрибуція on-behalf (C8) */}
              {isOnBehalf && (
                <div className="rounded-md border border-violet-foreground/20 bg-violet px-3 py-2.5 text-xs text-violet-foreground">
                  Звіт <b>від імені {onBehalfCtx!.managerName}</b> · причина зафіксована:
                  «{onBehalfCtx!.reason.length > 90 ? onBehalfCtx!.reason.slice(0, 90) + "…" : onBehalfCtx!.reason}».
                  Позначка «заповнив керівник» буде видима менеджеру та в Контролі команди.
                </div>
              )}

              {/* повернуто на уточнення (S1 review-цикл) */}
              {draft?.reviewStatus === "returned" && (
                <div className="flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2.5 text-xs text-warning-foreground">
                  <Undo2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    <b>Повернуто на уточнення керівником.</b>
                    {draft.reviewComment && <> Коментар: «{draft.reviewComment}»</>}
                    {" "}Після повторного подання звіт знову піде на перевірку.
                  </span>
                </div>
              )}

              {/* 1 · чи відбулась (on-behalf: приховано — supervisor подає звіт
                  про проведену зустріч; not-occurred лишається дією менеджера) */}
              <div hidden={isOnBehalf}>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">1 · Чи відбулась зустріч?</label>
                <div className="flex gap-2">
                  {([["yes", "Відбулась"], ["not", "Не відбулась"]] as const).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setOccurred(v)}
                      className={cn(
                        "inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                        occurred === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted",
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {occurred === "not" ? (
                <>
                  {/* причина не-проведення (§4: debrief/чіпи НЕ вимагаються) */}
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Причина</label>
                    <div className="flex flex-wrap gap-2">
                      {NOT_REASONS.map((r) => (
                        <button
                          key={r.v}
                          type="button"
                          onClick={() => setNotReason(r.v)}
                          className={cn(
                            "inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                            notReason === r.v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted",
                          )}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {notReason === "rescheduled" && (
                    <div className="rounded-md border px-3 py-3">
                      <div className="mb-1.5 text-xs font-medium text-muted-foreground">Нова дата та час (inline-перенесення)</div>
                      <div className="flex gap-2">
                        <Input type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} className="h-9 text-sm" />
                        <Input type="time" value={reschedTime} onChange={(e) => setReschedTime(e.target.value)} className="h-9 text-sm" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                      Коментар {notReason === "other" && <span className="text-destructive">* (обовʼязково для «Інша причина»)</span>}
                    </label>
                    <textarea
                      value={notComment}
                      onChange={(e) => setNotComment(e.target.value)}
                      rows={2}
                      placeholder={notReason === "rescheduled" ? "Чому переносимо (опц.)" : "Деталі (опц.)"}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>

                  <p className="flex items-center gap-1.5 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[11.5px] text-warning-foreground">
                    <Info className="h-3.5 w-3.5 shrink-0" /> Не відбулась → письмовий звіт і результат не вимагаються; фіксується лише причина.
                  </p>
                </>
              ) : (
                <>
                  {/* 2 · результат (чіпи поверх live outcome) — result-first
                      (Igor accept S2-fix: чіпи ДО тексту; risk-мінімум дебрифу
                      відомий до набору) */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                      2 · Результат <span className="text-destructive">*</span>
                    </label>
                    {OUTCOME_GROUPS.map((g) => (
                      <div key={g.title} className="mb-1.5">
                        <div className={cn("mb-1 text-[10.5px] font-bold uppercase tracking-wide", g.risk ? "text-destructive/80" : "text-muted-foreground/70")}>
                          {g.title}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {g.chips.map((c) => (
                            <button
                              key={c.v}
                              type="button"
                              onClick={() => { touch(); setOutcome(outcome === c.v ? null : c.v); }}
                              className={cn(
                                "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
                                outcome === c.v
                                  ? g.risk
                                    ? "border-destructive bg-destructive/10 text-destructive"
                                    : "border-primary bg-primary/10 text-primary"
                                  : "border-border text-muted-foreground hover:bg-muted",
                              )}
                            >
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {err?.code === "outcome_required" && (
                      <p role="alert" className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                        <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err.message}
                      </p>
                    )}
                  </div>

                  {/* ризик-маркери */}
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Ризик-маркери (опц.)</label>
                    <div className="flex flex-wrap gap-1.5">
                      {RISK_MARKERS.map((m) => (
                        <button
                          key={m.v}
                          type="button"
                          onClick={() => toggleMarker(m.v)}
                          className={cn(
                            "inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors",
                            riskMarkers.includes(m.v)
                              ? "border-destructive bg-destructive/10 text-destructive"
                              : "border-border text-muted-foreground hover:bg-muted",
                          )}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isRisk && (
                    <p className="flex items-start gap-1.5 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[11.5px] text-warning-foreground">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span><b>Ризиковий результат</b> → розширений опис (мін. {limits.minLengthRisk} симв.) + дія АБО явна причина «без дій». Тип дії не форсується.</span>
                    </p>
                  )}

                  {/* 3 · debrief — ОБОВ'ЯЗКОВИЙ ЗАВЖДИ при happened (D-054):
                      required* + лічильник + client-block submit до мін. довжини;
                      blacklist server-enforced (422) */}
                  <div>
                    <div className="mb-1.5 flex items-baseline justify-between">
                      <label className="text-xs font-medium text-muted-foreground">
                        3 · Короткий підсумок <span className="text-destructive">*</span>
                      </label>
                      <span className={cn("text-[11px] tabular-nums", len < minLen ? "text-warning-foreground" : "text-muted-foreground")}>
                        {len} / мін. {minLen}
                      </span>
                    </div>
                    <textarea
                      value={text}
                      onChange={(e) => { touch(); setText(e.target.value); }}
                      rows={4}
                      placeholder="Що обговорили, про що домовились, що важливо знати наступного разу?"
                      className={cn(
                        "w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                        debriefError && "border-destructive",
                      )}
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground opacity-80">
                      Обовʼязково, мін. {minLen} симв. «все ок» / «проведено» / «домовились» — не приймається (server-enforced).
                    </p>
                    {debriefError && (
                      <p role="alert" className="mt-1.5 flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[11.5px] text-destructive">
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /> {err!.message}
                      </p>
                    )}
                  </div>

                  {/* TASK-ATT v1.3 (W3) — вкладення: staged у server-draft,
                      atomic-bind на submit. W4 lock: після подання (поки не
                      повернуто) — view/download only. */}
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                      Вкладення (опц.)
                    </label>
                    {isOnBehalf ? (
                      /* P67 — tmp-транспорт: 0 Attachment/relation до submit */
                      <div className="space-y-1.5">
                        {tmpFiles.map((f) => (
                          <div key={f.tmpId} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs">
                            <span className="min-w-0 flex-1 truncate">{f.name}</span>
                            <span className="shrink-0 text-muted-foreground">{Math.max(1, Math.round(f.size / 1024))} КБ</span>
                            <button
                              type="button"
                              aria-label="Прибрати файл"
                              className="shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => {
                                void removeOnBehalfTmpFile({ clientRequestId: requestIdRef.current, tmpId: f.tmpId }).catch(() => {});
                                setTmpFiles((cur) => cur.filter((x) => x.tmpId !== f.tmpId));
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted">
                          {tmpUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                          Додати файл
                          <input
                            type="file"
                            hidden
                            disabled={tmpUploading || tmpFiles.length >= 10}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (!file) return;
                              setTmpUploading(true);
                              const reader = new FileReader();
                              reader.onload = () => {
                                const b64 = String(reader.result ?? "").split(",")[1] ?? "";
                                uploadOnBehalfTmpFile({
                                  clientRequestId: requestIdRef.current,
                                  name: file.name,
                                  type: file.type || "application/octet-stream",
                                  dataBase64: b64,
                                })
                                  .then((r) => setTmpFiles((cur) => [...cur, r]))
                                  .catch((err2) => setErr({
                                    code: "tmp_upload_failed",
                                    message: err2 instanceof ApiClientError ? err2.message : "Не вдалося додати файл",
                                  }))
                                  .finally(() => setTmpUploading(false));
                              };
                              reader.readAsDataURL(file);
                            }}
                          />
                        </label>
                        <p className="text-[11px] text-muted-foreground opacity-80">
                          Файли зберігаються в приватній tmp-зоні і привʼяжуться до зустрічі лише при поданні.
                        </p>
                      </div>
                    ) : (
                    <AttachmentUpload
                      parentType="CClientContactInteraction"
                      parentId={target.interactionId}
                      saved={savedAtt}
                      pending={pendingAtt}
                      onChange={onAttChange}
                      onRemoveSaved={onRemoveSaved}
                      disabled={attLocked}
                      label=""
                    />
                    )}
                  </div>

                  {/* 4 · наступна дія */}
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                      4 · Наступна дія <span className="text-destructive">*</span>
                    </label>
                    <div className="flex gap-2">
                      {([["action", "Створити дію"], ["none", "Без дій + причина"]] as const).map(([v, label]) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => { touch(); setDecision(v); }}
                          className={cn(
                            "inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                            decision === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted",
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {decision === "none" && (
                      <textarea
                        value={noActionReason}
                        onChange={(e) => { touch(); setNoActionReason(e.target.value); }}
                        rows={2}
                        placeholder="Чому дія не потрібна? (обовʼязково)"
                        className={cn(
                          "mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                          err?.code === "no_action_reason_required" && "border-destructive",
                        )}
                      />
                    )}

                    {decision === "action" && (
                      <div className="mt-2">
                        {/* S3 Actions Builder (visual v0.2 екран 4): staged-картки */}
                        <div className="mb-1.5 flex items-center justify-between">
                          <span className="text-[11.5px] text-muted-foreground">
                            Дії зі звіту — <b>чернетка</b>; створяться server-side ПІСЛЯ подання (одна транзакція)
                          </span>
                        </div>
                        <div className="mb-2 flex flex-wrap gap-1.5">
                          <button type="button" onClick={() => setStageDrawer("task")} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11.5px] font-medium hover:bg-muted">
                            <Plus className="h-3 w-3" /> Задача
                          </button>
                          <button type="button" onClick={() => setStageDrawer("follow_up")} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11.5px] font-medium hover:bg-muted">
                            <Plus className="h-3 w-3" /> Follow-up
                          </button>
                          <button type="button" onClick={() => setStageDrawer("internal_request")} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11.5px] font-medium hover:bg-muted">
                            <Plus className="h-3 w-3" /> Запит у відділ
                          </button>
                          <button type="button" onClick={() => setStageDrawer("suggestion")} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11.5px] font-medium hover:bg-muted">
                            <Plus className="h-3 w-3" /> Пропозиція профілю
                          </button>
                        </div>

                        {/* suggestion — inline staged mini-форма (§8 whitelist) */}
                        {stageDrawer === "suggestion" && (
                          <div className="mb-2 space-y-2 rounded-md border border-violet-foreground/20 bg-violet px-3 py-2.5">
                            <div className="text-[11.5px] font-semibold text-violet-foreground">Пропозиція оновлення профілю (review-flow, не перезапис)</div>
                            <select value={sugField} onChange={(e) => setSugField(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                              {SUGGESTION_FIELDS.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
                            </select>
                            <Input value={sugValue} onChange={(e) => setSugValue(e.target.value)} placeholder="Запропоноване значення" className="h-9 text-sm" />
                            <Input value={sugNote} onChange={(e) => setSugNote(e.target.value)} placeholder="Коментар (опц.)" className="h-9 text-sm" />
                            <div className="flex gap-2">
                              <button type="button" onClick={stageSuggestion} disabled={sugValue.trim() === ""} className="rounded-md border border-primary bg-primary/10 px-2.5 py-1.5 text-[11.5px] font-semibold text-primary disabled:opacity-50">
                                Додати до звіту
                              </button>
                              <button type="button" onClick={() => setStageDrawer(null)} className="rounded-md border px-2.5 py-1.5 text-[11.5px] text-muted-foreground">Скасувати</button>
                            </div>
                          </div>
                        )}

                        {stagedChecked.length === 0 ? (
                          <p className="rounded-md border border-dashed px-3 py-3 text-center text-[11.5px] text-muted-foreground">
                            Додайте задачу, follow-up, запит у відділ або пропозицію профілю. До submit нічого не створено.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {stagedChecked.map((c) => (
                              <div key={c.key} className={cn(
                                "rounded-md border px-2.5 py-2",
                                c.blocked && "border-destructive/50 bg-destructive/5",
                              )}>
                                <div className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                                  {c.kind === "internal_request" ? <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> : c.kind === "suggestion" ? <Lightbulb className="h-3.5 w-3.5 text-muted-foreground" /> : c.kind === "follow_up" ? <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" /> : <ListChecks className="h-3.5 w-3.5 text-muted-foreground" />}
                                  <span className="truncate">{c.title}</span>
                                  <span className={cn(
                                    "ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold",
                                    c.blocked ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
                                  )}>
                                    {c.blocked ? "blocked" : "staged"}
                                  </span>
                                  <button type="button" onClick={() => removeStaged(c.key)} aria-label="Видалити" className="shrink-0 text-muted-foreground hover:text-destructive">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <div className={cn("mt-0.5 text-[11px]", c.blocked ? "text-destructive" : "text-muted-foreground")}>
                                  {c.blocked ?? c.meta}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {blockedCards.length > 0 && (
                          <p role="alert" className="mt-2 flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[11.5px] text-destructive">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                            Запит у відділ неможливо створити — маршрут не налаштований. Звіт НЕ подається, поки дію не видалено/замінено.
                          </p>
                        )}
                        <p className="mt-1.5 rounded-md border border-violet-foreground/20 bg-violet px-3 py-2 text-[11px] text-violet-foreground">
                          Reuse canonical forms: <b>TaskCreateDrawer</b> (Action Engine) · <b>CreateInternalRequestDrawer</b> (#9, source=meeting_report) · <b>suggestion/review</b>. Staged → створення server-side на submit; route-missing → BLOCK.
                        </p>
                      </div>
                    )}
                    {err?.code === "no_action_reason_required" && (
                      <p role="alert" className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-destructive">
                        <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err.message}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* S6 impl-state: submit_transaction_failed — ОКРЕМИЙ стан (visual
                  LOCK): atomic submit відкотився ПОВНІСТЮ — звіт НЕ подано. */}
              {err?.code === "submit_transaction_failed" ? (
                <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-xs text-destructive">
                  <p className="flex items-center gap-1.5 font-semibold">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Дію не вдалося створити — звіт НЕ подано (повний відкат).
                  </p>
                  <p className="mt-1">
                    Жодних напівстанів: ні звіту, ні дій. Чернетка збережена — перевірте
                    staged-дії та подайте ще раз.
                  </p>
                </div>
              ) : err && !debriefError && !["outcome_required", "no_action_reason_required"].includes(err.code) && (
                /* generic server error (не привʼязана до поля) */
                <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err.message}
                </p>
              )}
            </div>

            <div className="sticky bottom-0 border-t bg-background px-5 py-3">
              {occurred === "yes" && !isOnBehalf && (
                <div className="mb-2 flex h-4 items-center text-[11px] text-muted-foreground" aria-live="polite">
                  {draftState === "saving" && (<><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Збереження чернетки…</>)}
                  {draftState === "saved" && "Чернетку збережено"}
                  {draftState === "error" && <span className="text-warning-foreground">Помилка автозбереження — повторимо при наступній зміні</span>}
                  {draftState === "conflict" && <span className="text-destructive">Чернетку змінено в іншій вкладці — закрийте і відкрийте форму</span>}
                </div>
              )}
              <Button type="button" onClick={submit} disabled={!canSubmit} className="h-11 w-full text-sm font-semibold">
                {mutation.isPending
                  ? (<><Loader2 className="h-4 w-4 animate-spin" /> Подання…</>)
                  : occurred === "yes" ? "Подати звіт" : notReason === "rescheduled" ? "Перенести зустріч" : "Зафіксувати причину"}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* S3 — staged-mode canonical дровери (P7: повертають spec, НЕ POST-ять) */}
      <TaskCreateDrawer
        open={stageDrawer === "task" || stageDrawer === "follow_up"}
        onClose={() => setStageDrawer(null)}
        entry={{ defaultSubtype: stageDrawer === "follow_up" ? "follow_up" : "generic", source: "Звіт зустрічі (staged)" }}
        staged={{ onStage: stageTask }}
      />
      <CreateInternalRequestDrawer
        open={stageDrawer === "internal_request"}
        onClose={() => setStageDrawer(null)}
        sourceModule="meeting_report"
        sourceEntityId={target.interactionId}
        staged={{ onStage: stageRequest }}
      />
    </div>
  );
}
